import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, SlidersHorizontal } from "lucide-react";
import { MENU, TAB_ORDER, COMPARE, ALLERGEN_FILTERS, ALLERGEN_TAGS } from "@/data/menu";
import NutritionCard from "@/components/site/NutritionCard";
import { Reveal } from "@/components/site/Reveal";

const ICONS = { preDrinks: "🔥", preMeals: "🍽️", postDrinks: "💪", postMeals: "🥗", rtd: "🥫" };

function ComparisonTable() {
  const rows = [
    ["Calories", (i) => `${i.calories}`, "kcal"],
    ["Protein", (i) => i.protein, "g", true],
    ["Carbs", (i) => i.carbs, "g"],
    ["Fat", (i) => i.fat, "g"],
    ["Fiber", (i) => i.fiber, "g"],
    ["Sodium", (i) => i.sodium, "mg"],
  ];
  const maxProtein = Math.max(...COMPARE.map((i) => i.protein));
  return (
    <div className="overflow-x-auto rounded-3xl border border-line" data-testid="comparison-table">
      <table className="w-full min-w-[640px] text-left">
        <thead>
          <tr className="bg-beige">
            <th className="p-5 font-display text-lg text-coffee">Nutrient</th>
            {COMPARE.map((i) => (
              <th key={i.name} className="p-5 font-semibold text-sm text-coffee align-bottom">{i.name}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, get, unit, hl]) => (
            <tr key={label} className="border-t border-line">
              <td className="p-5 text-sm uppercase tracking-wide text-coffee-muted font-medium">{label}</td>
              {COMPARE.map((i) => {
                const best = hl && get(i) === maxProtein;
                return (
                  <td key={i.name} className="p-5 font-mono text-coffee">
                    <span className={best ? "text-teal-brand font-bold" : ""}>{get(i)}{unit}</span>
                    {best && <span className="ml-1 text-[10px] uppercase text-teal-brand">best</span>}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function MenuSection() {
  const [tab, setTab] = useState("preDrinks");
  const [diet, setDiet] = useState([]);
  const tabs = [...TAB_ORDER, "compare"];
  const active = tab === "compare" ? null : MENU[tab];

  const toggleDiet = (key) => setDiet((d) => (d.includes(key) ? d.filter((k) => k !== key) : [...d, key]));
  const passes = (item) =>
    diet.every((key) => {
      if (key === "vegan") return item.vegan === true;
      if (key === "vegetarian") return item.vegetarian === true || item.vegan === true;
      return !(ALLERGEN_TAGS[item.name] || []).includes(key);
    });
  const visible = active ? active.items.filter(passes) : [];

  return (
    <section id="menu" data-testid="menu-section" className="bg-beige py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-bold uppercase tracking-[0.25em] text-teal-brand">The Menu</p>
          <h2 className="mt-3 font-display text-4xl sm:text-6xl font-bold tracking-tight text-coffee">
            Every item, every macro.
          </h2>
          <p className="mt-4 text-lg text-coffee-soft">
            News-style tabs to jump between pre &amp; post-workout drinks and meals — with full nutrient
            transparency baked into every card.
          </p>
        </Reveal>

        <div className="mt-10 flex flex-wrap gap-2.5" data-testid="menu-tabs">
          {tabs.map((key) => {
            const on = tab === key;
            const label = key === "compare" ? "📊 Nutrition Comparison" : `${ICONS[key]} ${MENU[key].label}`;
            return (
              <button
                key={key}
                data-testid={`tab-${key}`}
                onClick={() => setTab(key)}
                className={`rounded-full px-5 py-2.5 text-sm font-semibold border transition-colors ${
                  on ? "bg-[#8A2231] text-white border-teal-brand" : "border-line text-coffee-soft hover:border-teal-brand"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {tab !== "compare" && (
          <div className="mt-6 flex flex-wrap items-center gap-2.5" data-testid="allergen-filter">
            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-coffee-muted mr-1">
              <SlidersHorizontal size={15} /> Dietary
            </span>
            {ALLERGEN_FILTERS.map((f) => {
              const on = diet.includes(f.key);
              return (
                <button
                  key={f.key}
                  data-testid={`diet-${f.key}`}
                  onClick={() => toggleDiet(f.key)}
                  className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm font-medium transition-colors ${
                    on ? "bg-[#8A2231] text-white border-[#8A2231]" : "border-line text-coffee-soft hover:border-teal-brand"
                  }`}
                >
                  {on && <Check size={14} />} {f.label}
                </button>
              );
            })}
            {diet.length > 0 && (
              <button data-testid="diet-clear" onClick={() => setDiet([])} className="text-sm text-teal-brand font-semibold underline underline-offset-4 ml-1">
                Clear
              </button>
            )}
          </div>
        )}
        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.4 }}
            className="mt-10"
          >
            {tab === "compare" ? (
              <>
                <p className="mb-6 text-coffee-soft">Compare our signature post-workout meals side-by-side.</p>
                <ComparisonTable />
              </>
            ) : (
              <>
                <p className="mb-8 font-display italic text-xl text-teal-brand">{active.tagline}</p>
                {visible.length === 0 ? (
                  <div data-testid="menu-empty" className="rounded-3xl border border-dashed border-line py-20 text-center">
                    <p className="font-display text-2xl text-coffee">No items match those filters</p>
                    <p className="mt-2 text-coffee-soft">Try removing a dietary filter to see more options.</p>
                  </div>
                ) : (
                  <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
                    {visible.map((item, i) => (
                      <NutritionCard key={item.name} item={item} index={`${tab}-${i}`} />
                    ))}
                  </div>
                )}
              </>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
